import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
    Camera, Sparkle, ArrowsCounterClockwise, DownloadSimple, Lock, X,
} from "@phosphor-icons/react";
import BeforeAfterSlider from "@/components/site/BeforeAfterSlider";

const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
    });

export default function SmileSimulator({ defaultEmail = "", attributedDoctorId = null, onSimulate }) {
    const inputRef = useRef(null);
    const [originalSrc, setOriginalSrc] = useState(null);
    const [editedSrc, setEditedSrc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showEmailGate, setShowEmailGate] = useState(false);
    const [email, setEmail] = useState(defaultEmail);
    const [unlocked, setUnlocked] = useState(false);

    const onPick = () => inputRef.current?.click();

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please upload an image file.");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Photo too large. Please pick one under 8MB.");
            return;
        }

        setLoading(true);
        setEditedSrc(null);
        setUnlocked(false);
        try {
            const b64 = await fileToBase64(file);
            setOriginalSrc(`data:${file.type};base64,${b64}`);

            const { data } = await api.post("/smile-simulator/transform", { image_base64: b64 });
            setEditedSrc(`data:${data.mime_type};base64,${data.edited_image_base64}`);
            // Analytics hook — lets a wrapping page (e.g. doctor landing) log "simulate" events.
            if (typeof onSimulate === "function") onSimulate();
        } catch (err) {
            const detail = err?.response?.data?.detail || "Something went wrong. Please try a different photo.";
            toast.error(detail);
            setOriginalSrc(null);
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setOriginalSrc(null);
        setEditedSrc(null);
        setUnlocked(false);
        if (inputRef.current) inputRef.current.value = "";
    };

    const downloadResult = () => {
        if (!editedSrc) return;
        const a = document.createElement("a");
        a.href = editedSrc;
        a.download = "my-invisique-smile-preview.png";
        a.click();
    };

    const handleUnlock = async () => {
        const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!valid) {
            toast.error("Please enter a valid email.");
            return;
        }
        try {
            await api.post("/smile-assessment", {
                name: "Smile Simulator User",
                phone: "n/a",
                email,
                city: "n/a",
                concerns: ["smile-simulator-download"],
                attributed_doctor_id: attributedDoctorId || undefined,
            });
        } catch (err) {
            // Soft-fail: still let the user download even if logging fails
            console.error("Smile simulator email-gate logging failed:", err);
        }
        setUnlocked(true);
        setShowEmailGate(false);
        toast.success("Result unlocked! Download or share your new smile.");
    };

    // No upload yet, show the dropzone CTA
    if (!originalSrc) {
        return (
            <div className="bg-card border border-border rounded-3xl p-6 sm:p-10 bento-shadow" data-testid="smile-simulator-zone">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-[#003E50] flex items-center justify-center shrink-0 shadow-lg">
                        <Sparkle size={22} weight="fill" color="white" />
                    </div>
                    <div>
                        <h3 className="font-display text-2xl font-bold tracking-tight">
                            See your new smile in <span className="text-primary">10 seconds</span>
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Upload a clear, front-facing selfie smiling and our AI will preview your post-Invisique™ smile.
                        </p>
                    </div>
                </div>

                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    className="hidden"
                    onChange={onFile}
                    data-testid="smile-simulator-input"
                />
                <button
                    onClick={onPick}
                    disabled={loading}
                    className="group w-full rounded-2xl border-2 border-dashed border-primary/30 hover:border-primary bg-primary/5 hover:bg-primary/10 p-10 text-center transition-all"
                    data-testid="smile-simulator-pick-btn"
                >
                    <div className="w-14 h-14 mx-auto rounded-full bg-white border border-primary/20 flex items-center justify-center group-hover:scale-105 transition-transform shadow-sm">
                        <Camera size={26} weight="duotone" color="hsl(192 100% 31%)" />
                    </div>
                    <div className="font-display text-lg font-semibold mt-4">Tap to upload your selfie</div>
                    <div className="text-xs text-muted-foreground mt-1.5">JPG / PNG · max 8MB · processed securely on your device</div>
                </button>

                <div className="mt-5 grid sm:grid-cols-3 gap-2 text-[11px] text-muted-foreground">
                    <div>📸 Smile naturally with teeth visible</div>
                    <div>💡 Use good lighting, no shadows</div>
                    <div>👤 Face the camera straight on</div>
                </div>
            </div>
        );
    }

    // Loading the AI transformation
    if (loading) {
        return (
            <div className="bg-card border border-border rounded-3xl p-10 bento-shadow text-center" data-testid="smile-simulator-loading">
                <div className="relative w-16 h-16 mx-auto mb-5">
                    <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
                    <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-primary to-[#003E50] flex items-center justify-center shadow-xl">
                        <Sparkle size={28} weight="fill" color="white" />
                    </div>
                </div>
                <h3 className="font-display text-2xl font-bold">Crafting your new smile...</h3>
                <p className="text-sm text-muted-foreground mt-2">
                    Our AI is straightening, whitening and previewing your post-treatment smile.
                </p>
                <p className="text-[11px] text-muted-foreground mt-4 italic">Usually takes 10 to 20 seconds</p>
            </div>
        );
    }

    // Result, original + edited side by side, with email gate before download
    return (
        <div className="bg-card border border-border rounded-3xl p-6 sm:p-8 bento-shadow" data-testid="smile-simulator-result">
            <div className="flex items-start justify-between gap-3 mb-5">
                <div>
                    <div className="overline text-primary mb-1.5">Your AI smile preview</div>
                    <h3 className="font-display text-2xl font-bold">Drag to reveal your new smile</h3>
                </div>
                <button
                    onClick={reset}
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 px-3 py-1.5 rounded-full border border-border hover:border-primary transition-colors"
                    data-testid="smile-simulator-reset-btn"
                >
                    <ArrowsCounterClockwise size={12} /> Try another photo
                </button>
            </div>

            <div className="relative">
                {editedSrc ? (
                    <BeforeAfterSlider before={originalSrc} after={editedSrc} beforeLabel="Today" afterLabel="With Invisique™" />
                ) : null}

                {/* Watermark when locked */}
                {!unlocked && editedSrc && (
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                        <div
                            className="font-display text-white/30 text-2xl sm:text-4xl font-bold tracking-widest rotate-[-18deg] select-none"
                            style={{ textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}
                        >
                            INVISIQUE™ PREVIEW
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row items-center gap-3">
                {!unlocked ? (
                    <Button
                        onClick={() => setShowEmailGate(true)}
                        className="w-full sm:w-auto h-12 px-6 rounded-full bg-gradient-to-r from-primary to-[#003E50] text-white font-semibold tracking-wide hover:from-[#003E50] hover:to-primary"
                        data-testid="smile-simulator-unlock-btn"
                    >
                        <Lock size={16} weight="fill" className="mr-2" />
                        Unlock & download
                    </Button>
                ) : (
                    <Button
                        onClick={downloadResult}
                        className="w-full sm:w-auto h-12 px-6 rounded-full"
                        data-testid="smile-simulator-download-btn"
                    >
                        <DownloadSimple size={16} weight="bold" className="mr-2" />
                        Download my preview
                    </Button>
                )}
                <p className="text-[11px] text-muted-foreground text-center sm:text-left">
                    AI preview only, your real result is determined by your certified Invisique™ doctor.
                </p>
            </div>

            {/* Email gate modal */}
            {showEmailGate && (
                <div className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm flex items-center justify-center p-5" data-testid="email-gate-modal">
                    <div className="bg-card rounded-3xl p-6 sm:p-8 max-w-md w-full relative">
                        <button
                            onClick={() => setShowEmailGate(false)}
                            className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-muted"
                            aria-label="Close"
                        >
                            <X size={16} weight="bold" />
                        </button>
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                            <Sparkle size={22} weight="fill" color="hsl(192 100% 31%)" />
                        </div>
                        <h4 className="font-display text-2xl font-bold mb-1.5">One step away</h4>
                        <p className="text-sm text-muted-foreground mb-5">
                            Enter your email to unlock the full-resolution image and receive smile-care tips from our doctors.
                        </p>
                        <Label htmlFor="sim-email">Email</Label>
                        <Input
                            id="sim-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@email.com"
                            className="h-12 mt-1.5 rounded-xl"
                            data-testid="email-gate-input"
                        />
                        <Button
                            onClick={handleUnlock}
                            className="w-full h-12 mt-4 rounded-full bg-gradient-to-r from-primary to-[#003E50] text-white font-semibold"
                            data-testid="email-gate-submit-btn"
                        >
                            Unlock my preview
                        </Button>
                        <p className="text-[10px] text-muted-foreground text-center mt-3">
                            We'll never share your email. Unsubscribe anytime.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
